// Tipos relacionados a mensagens

import { MessageSender, MessageStatus } from "./common.types";

export type MessageTypeEnum =
  | "text"
  | "image"
  | "button"
  | "image_text"
  | "button_text"
  | "reaction"
  | "unsupported"
  | "template"
  | "voice"
  | "audio"
  | "video"
  | "document"
  | "sticker";

export interface MessageContent {
  imageData?: {
    mimeType: string;
    sha256: string;
    mediaId: string;
    originalUrl: string;
    caption: string;
    error?: string;
  };
  text?: string;
  image?: string;
  caption?: string;
  file?: string;
  buttons?: Array<{
    id: string;
    text: string;
    type: "reply" | "url";
    url?: string;
  }>;
  reaction?: {
    message_id: string;
    emoji: string;
  };
  templateData?: {
    templateId: string;
    templateName: string;
    templateLanguage: string;
    components?: Array<Record<string, unknown>>;
    header?: string;
    body?: string;
    buttons?: Array<Record<string, unknown>>;
    imageUrl?: string;
  };
  voiceData?: {
    url?: string;
    mediaUrl?: string;
    duration?: number;
    mimeType?: string;
    sha256?: string;
    mediaId?: string;
    error?: string;
  };
  documentData?: {
    url?: string;
    mediaUrl?: string;
    filename?: string;
    mimeType?: string;
    sha256?: string;
    mediaId?: string;
    error?: string;
  };
  videoData?: {
    url?: string;
    mediaUrl?: string;
    caption?: string;
    mimeType?: string;
    sha256?: string;
    mediaId?: string;
    error?: string;
  };
  errors?: Array<{
    code: number;
    details: string;
    title: string;
  }>;
}

export interface WhatsAppMessageStatus {
  status: MessageStatus;
  timestamp?: string;
  conversation?: {
    id: string;
    origin: {
      type: string;
    };
  };
  pricing?: {
    billable: boolean;
    pricing_model: string;
    category: string;
  };
  recipient_id?: string;
  raw_status?: {
    id: string;
    status: MessageStatus;
    timestamp: string;
    recipient_id: string;
    conversation: {
      id: string;
      origin: {
        type: string;
      };
    };
    pricing: {
      billable: boolean;
      pricing_model: string;
      category: string;
    };
  };
}

export interface MessageType {
  _id?: string;
  from?: string;
  to?: string;
  content: MessageContent;
  type: MessageTypeEnum;
  timestamp: Date;
  status?: MessageStatus;
  statuses?: Array<{
    status: MessageStatus;
    timestamp: Date;
  }>;
  sentBy: MessageSender;
  readStatus?: {
    isRead: boolean;
    readBy?: {
      id: string;
      name: string;
    };
    readAt?: Date;
    lastUpdatedAt?: string;
    _id?: string;
  };
  rawWebhookData?: {
    object: string;
    entry: Array<{
      id: string;
      changes: Array<{
        value: {
          messaging_product: string;
          metadata: {
            display_phone_number: string;
            phone_number_id: string;
          };
          contacts: Array<{
            profile: {
              name: string;
            };
            wa_id: string;
          }>;
          messages: Array<{
            from: string;
            id: string;
            timestamp: string;
            text?: {
              body: string;
            };
            type: string;
          }>;
        };
        field: string;
      }>;
    }>;
  };
  user?: {
    id: string;
    name: string;
  };
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
}

export interface PaginatedMessagesResponse {
  messages: MessageType[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// Função auxiliar para obter o timestamp mais antigo dos statuses
export const getMessageTimestamp = (message: MessageType): Date => {
  const timestamp = message.timestamp || (message as any).createdAt;

  if (timestamp) {
    if (timestamp instanceof Date) {
      return timestamp;
    }

    if (typeof timestamp === "number") {
      return new Date(timestamp);
    }

    // Se for string, verificar se é ISO e se falta o fuso
    let dateStr = String(timestamp);
    if (
      dateStr.includes("T") &&
      !dateStr.includes("Z") &&
      !dateStr.includes("+") &&
      !/-\d{2}:\d{2}$/.test(dateStr)
    ) {
      dateStr += "Z";
    }

    const dateObj = new Date(dateStr);
    return isNaN(dateObj.getTime()) ? new Date() : dateObj;
  }

  return new Date();
};

export interface MessageData {
  to: string;
  type: "file" | "text";
  content: {
    text: string;
    files: Array<Record<string, unknown>>;
  };
  userId: string;
  companyId: string;
  disableAI: boolean;
}

export interface Message {
  id: string;
  content: string;
  timestamp: Date;
  status: MessageStatus;
  sentBy: "user" | "client";
}

export interface MessageGroup {
  date: string;
  messages: MessageType[];
}

export interface CachedMessages {
  messages: MessageType[];
  timestamp: number;
  phoneNumber: string;
  lastMessageId?: string;
}

export interface SendMessageParams {
  to: string;
  type: string;
  content: {
    text: string;
  };
  userId: string;
  companyId: string;
  disableAI?: boolean;
}
